import { NgClass } from "@angular/common";
import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ActionButtonComponent } from "@components/action-button/action-button.component";
import { HeaderComponent } from "@components/header/header.component";
import { InputNumberComponent } from "@components/input-number/input-number.component";
import { InputComponent } from "@components/input/input.component";
import { SelectorComponent } from "@components/selector/selector.component";
import { TreeSelectorComponent as TreeSelectorComponent_1 , TreeSelectorComponent } from "@components/tree-selector/tree-selector.component";
import { NgbCollapse } from "@ng-bootstrap/ng-bootstrap";
import { cloneDeep } from "lodash";
import { BehaviorSubject } from "rxjs";
import { SelectionEvent } from "~app/common/components/tree-selector/SelectionEvent";
import { SelectionStrategyType } from "~app/common/components/tree-selector/SelectionStrategyType";

import { ObjectUtils } from "~app/common/utils/object-utils";
import { ValidationMessageRowComponent } from "../../../../common/validation/validation-message-row/validation-message-row.component";
import { IHandelssoritmentgruppInformation } from "../../arbetsorder-produktval/arbetsorder-produktval.models";
import { IProduktionsmall } from "../../produktionsmallar/produktionsmall-produktval.models";
import {
    IHandelssortiment,
    IHandelssortimentsgrupp,
    IProduktvalRow,
    IProduktvalRowProduktInstruktion,
    ProduktSelection,
    ProduktvalEvent,
    ProduktvalSelectionItem
} from "../produktval.models";
import { ProduktvalTableTexts } from "./produktval-table.texts";

@Component({
    selector: "rask-produktval-table",
    templateUrl: "./produktval-table.component.html",
    styleUrls: ["./produktval-table.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        HeaderComponent,
        ActionButtonComponent,
        TreeSelectorComponent_1,
        FormsModule,
        NgClass,
        InputNumberComponent,
        NgbCollapse,
        SelectorComponent,
        InputComponent,
        ValidationMessageRowComponent
    ]
})
export class ProduktvalTableComponent implements OnInit, OnChanges {
    @Input() layout: "div" | "table";
    @Input() handelssortimentsgruppInformation: IHandelssoritmentgruppInformation;
    @Input() handelssortimentsgrupper: IHandelssortiment[];
    @Input() mall: IProduktionsmall;
    @Input() produktvalsrader: IProduktvalRow[];
    @Input() visaEndastMedProduktionsmal: boolean;
    @Input() isProduktvalApplicable: boolean;
    @Input() isSimuleringApplicable: boolean;
    @Input() allahandelssortimentsgrupper: IHandelssortimentsgrupp[];
    @Input() onBeforeSelectionEvent: ($event: ProduktvalEvent) => Promise<boolean>;
    @Input() canEdit: boolean;

    // eslint-disable-next-line @angular-eslint/no-output-rename, @angular-eslint/no-output-on-prefix
    @Output("onSelectionEvent") selectionEvent = new EventEmitter<ProduktvalEvent>();

    @ViewChild("tree") treeSelector: TreeSelectorComponent;

    onTreeBeforeSelectionEvent: ($event: SelectionEvent) => Promise<boolean>;
    selectionStrategyType = SelectionStrategyType.SelectFirstLeafIfOnlyOne;
    treeSelectorValues: ProduktvalSelectionItem[];
    result: string[] = [];
    visibleTreeselectorValues: BehaviorSubject<ProduktvalSelectionItem[]> = new BehaviorSubject<ProduktvalSelectionItem[]>(null);
    texts = ProduktvalTableTexts.texts;

    private readonly hsMap: { [key: string]: ProduktSelection } = {};

    get hasRows(): boolean {
        return this.produktvalsrader && this.produktvalsrader.length > 0;
    }

    get isDivLayout(): boolean {
        return this.layout !== "table";
    }

    get volymAttFordela(): number {
        if (!this.handelssortimentsgruppInformation?.handelssortimentsgruppVolym || this.isSimuleringApplicable) {
            return undefined;
        }

        const totFordelat = this.produktvalsrader?.reduce((sum, current) => sum + current.kvantitet, 0);

        return this.handelssortimentsgruppInformation?.handelssortimentsgruppVolym - (totFordelat ? totFordelat : 0);
    }

    get visaFordelaVolym(): boolean {
        return !ObjectUtils.isNullOrUndefined(this.volymAttFordela);
    }

    treeSelectorSortExpression = (a, b) => (a.text > b.text ? 1 : -1);

    ngOnInit() {
        if (this.onBeforeSelectionEvent) {
            this.onTreeBeforeSelectionEvent = async ($event: SelectionEvent): Promise<boolean> => {
                const element = this.hsMap[$event.id];

                const produktvalEvent = new ProduktvalEvent();
                produktvalEvent.row = element;
                produktvalEvent.selectionEventType = $event.type;

                return this.onBeforeSelectionEvent(produktvalEvent);
            };
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.handelssortimentsgrupper && changes.handelssortimentsgrupper.currentValue !== changes.handelssortimentsgrupper.previousValue) {
            this.handelssortimentsGrupperChanged();
        }

        if (this.produktvalsrader) {
            this.result = this.produktvalsrader
                .map(g => g.getSelectionIds())
                .reduce((prev, curr) => prev.concat(curr), []);

            if (this.result && this.treeSelector) {
                this.treeSelector.writeValue(this.result);
            }
        }

        if (this.result.length > 0 && !this.produktvalsrader && this.treeSelector) {
            // Om vi har result sedan tidigare men inte längre så rensar vi selectorn
            this.result = [];
            this.treeSelector.writeValue(this.result);
        }
        this.filterTreeselectorValues();
    }

    handelssortimentsGrupperChanged() {
        this.treeSelectorValues = this.handelssortimentsgrupper?.map(g => {
            const item = new ProduktvalSelectionItem();
            item.hasProduktionsmal = g.hasProduktionsmal;
            item.relaterarTillId = g.relaterarTillHandelssortimentId;
            item.aktiv = g.aktiv || g.selectedInAo;
            item.text = !g.aktiv ? `${g.namn  } (Inaktiv)` : g.namn;
            item.id = g.handelssortimentId;
            item.value = `${g.handelssortimentId}`;
            this.hsMap[item.value] = new ProduktSelection(
                this.handelssortimentsgruppInformation?.handelssortimentsgruppNamn,
                g
            );
            item.children = g.produkter.map(pr => {
                const produkt = new ProduktvalSelectionItem();
                produkt.id = pr.produktId;
                produkt.parent = item;
                produkt.aktiv = pr.aktiv || pr.selectedInAo;
                produkt.text = !pr.aktiv ? `${pr.namn  } (Inaktiv)` : pr.namn;
                produkt.value = `${g.handelssortimentId}:${pr.produktId}`;
                this.hsMap[produkt.value] = new ProduktSelection(
                    this.handelssortimentsgruppInformation?.handelssortimentsgruppNamn,
                    g,
                    pr
                );
                produkt.children = pr.produktinstruktioner.map(pin => {
                    const prin = new ProduktvalSelectionItem();
                    prin.parent = produkt;
                    prin.aktiv = pin.aktiv || pin.selectedInAo;
                    prin.text = `${pin.namn} (${pin.puid.replace("SVEASKOG", "")})`;
                    prin.value = `${g.handelssortimentId}:${pr.produktId}:${pin.produktinstruktionId}`;
                    prin.tooltipText = `${pin.namn} PUID:  ${pin.puid}`;
                    this.hsMap[prin.value] = new ProduktSelection(
                        this.handelssortimentsgruppInformation?.handelssortimentsgruppNamn,
                        g,
                        pr,
                        pin
                    );
                    prin.children = undefined;

                    return prin;
                });

                if (this.produktvalsrader) {
                    const valdProdukt = this.produktvalsrader.find(x => x.handelssortimentId === g.handelssortimentId && x.produktId === pr.produktId);

                    if (valdProdukt && valdProdukt.hasPinnar) {
                        const isMatch = (pin: IProduktvalRowProduktInstruktion): boolean => produkt.children
                        // eslint-disable-next-line sonarjs/no-nested-functions
                            .some(y => y.value === `${valdProdukt.handelssortimentId}:${valdProdukt.produktId}:${pin.id}`);
                        const notPresentInList = valdProdukt.produktinstruktioner.filter(x => !isMatch(x));
                        notPresentInList.forEach(x => {
                            const prin = new ProduktvalSelectionItem();
                            prin.aktiv = false;
                            prin.selected = true;
                            prin.text = `${x.namn} (${x.puid.replace("SVEASKOG", "")})`;
                            prin.value = `${g.handelssortimentId}:${pr.produktId}:${x.id}`;
                            prin.tooltipText = `${x.namn} PUID:  ${x.puid}`;
                            this.hsMap[prin.value] = new ProduktSelection(
                                this.handelssortimentsgruppInformation?.handelssortimentsgruppNamn,
                                g,
                                pr,
                                {
                                    produktinstruktionId: x.id,
                                    namn: x.namn,
                                    puid: x.puid
                                }
                            );
                            prin.children = undefined;
                            produkt.children.push(prin);
                        });
                    }
                }

                return produkt;
            });

            return item;
        });
        this.filterAfterHandelssortimentGruppChanged();
    }

    onJusteradKvantitetChange($event: any, produktvalsrad: IProduktvalRow) {
        produktvalsrad.setJusteradKvantitet($event);
    }

    onMedTillUtbytessimuleringChange($event: any, produktvalsrad: IProduktvalRow) {
        if ($event === produktvalsrad.medTillUtbytessimulering) {
            return;
        }

        produktvalsrad.setMedTillUtbytessimulering($event);
    }

    onTreeSelectionEvent($event: SelectionEvent) {
        const element = this.hsMap[$event.id];
        const produktvalEvent = new ProduktvalEvent();
        produktvalEvent.row = element;
        produktvalEvent.selectionEventType = $event.type;
        this.selectionEvent.emit(produktvalEvent);
    }

    antalPin(produktvalsrad: IProduktvalRow): number {
        if (produktvalsrad && produktvalsrad.produktinstruktioner) {
            return produktvalsrad.produktinstruktioner.length;
        } else {
            return 0;
        }
    }

    showTree($event) {
        this.treeSelector.toggle();
    }

    onHeaderClick(produktvalsrad) {
        if (this.isProduktvalApplicable) {
            produktvalsrad.collapsed = !produktvalsrad.collapsed;
        }
    }

    getCheckedClass(produktvalsrad: IProduktvalRow): string {
        const antalPin = this.antalPin(produktvalsrad);
        let klass = "";

        if (antalPin === 1) {
            klass = "fa-check";
        } else if (antalPin > 1) {
            klass = "fa-check-double";
        }

        if (produktvalsrad.medTillUtbytessimulering) {
            klass += " " + "checked";
        }

        return klass;
    }

    private filterAfterHandelssortimentGruppChanged() {
        this.treeSelectorValues = this.filterInaktivaHandelssortimentNotInProduktionsmall(this.treeSelectorValues);
        this.treeSelectorValues = this.filterInaktivaProdukterNotInProduktionsmall(this.treeSelectorValues);
        this.visibleTreeselectorValues.next(this.treeSelectorValues);

        if (this.produktvalsrader) {
            this.result = this.produktvalsrader
                .map(g => g.getSelectionIds())
                .reduce((prev, curr) => prev.concat(curr), []);
        }

        this.filterTreeselectorValues();
    }

    private filterTreeselectorValues() {
        if (!this.treeSelectorValues) {
            return;
        }

        if (this.visaEndastMedProduktionsmal) {
            let filteredHandelssortiment = this.getSelectedHandelssortiment();
            filteredHandelssortiment = filteredHandelssortiment.concat(this.getHandelsortimentWithProduktionsmal());
            filteredHandelssortiment = filteredHandelssortiment.concat(this.getRelateradehandelsortiment(filteredHandelssortiment));

            this.visibleTreeselectorValues.next(this.getUniquelist(filteredHandelssortiment));
        } else {
            this.visibleTreeselectorValues.next(this.treeSelectorValues);
        }
    }

    private filterInaktivaHandelssortimentNotInProduktionsmall(treeSelectorValues: ProduktvalSelectionItem[]): ProduktvalSelectionItem[] {
        const allHandelssortimentIdInMall = this.mall?.handelssortiment.map(hs => hs.handelssortimentId) ?? [];
        return treeSelectorValues.filter(value => value.aktiv || allHandelssortimentIdInMall.includes(value.id));
    }

    private filterInaktivaProdukterNotInProduktionsmall(treeSelectorValues: ProduktvalSelectionItem[]): ProduktvalSelectionItem[] {
        const allProduktIdInMall = (this.mall?.handelssortiment ?? []).map(hs => hs.produkter.map(p => p.produktId)).flat();
        const filteredList = [] as ProduktvalSelectionItem[];
        treeSelectorValues.forEach(val => {
            if (val.aktiv || val.children.some(childProdukt => allProduktIdInMall.includes(childProdukt.id))) {
                const currentProduktval = cloneDeep(val);
                currentProduktval.children = [];
                val.children.forEach(child => {
                    if (child.aktiv || allProduktIdInMall.includes(child.id)) {
                        child.children = child.children?.filter(c => c.aktiv || (!c.aktiv && c.selected)) ?? [];
                        currentProduktval.children.push(child);
                    }
                });
                filteredList.push(currentProduktval);
            }
        });
        return filteredList;
    }

    private getUniquelist(list: any[]) {
        const uniqueIds = Array.from(new Set(list.map(x => x.id)));
        let uniqueHandelssortimentList = [];
        uniqueIds.forEach(x => {
            uniqueHandelssortimentList = uniqueHandelssortimentList.concat(
                list.find(y => y.id === x)
            );
        });

        return uniqueHandelssortimentList;
    }

    private getSelectedHandelssortiment() {
        let filteredHandelssortiment = [];

        if (this.produktvalsrader) {
            this.produktvalsrader.forEach(x => {
                filteredHandelssortiment = filteredHandelssortiment.concat(
                    this.treeSelectorValues.filter(
                        y => y.id === x.handelssortimentId
                    )
                );
            });
        }

        return filteredHandelssortiment;
    }

    private getHandelsortimentWithProduktionsmal() {
        return this.treeSelectorValues.filter(x => x.hasProduktionsmal);
    }

    private getRelateradehandelsortiment(handelssortimentLista: any[]) {
        this.visibleTreeselectorValues.value.forEach(x => {
            if (this.isRelatedToHandelsortimentWithProduktionsmal(x.relaterarTillId)) {
                handelssortimentLista = handelssortimentLista.concat(x);
            }
        });

        return handelssortimentLista;
    }

    private isRelatedToHandelsortimentWithProduktionsmal(relaterarTillHandelssortimentId: number) {
        if (relaterarTillHandelssortimentId) {
            return this.allahandelssortimentsgrupper.some(x => x.handelssortiment.find(y => y.handelssortimentId === relaterarTillHandelssortimentId && y.hasProduktionsmal));
        }
        return false;
    }
}
